/** Post-authentication destination based on platform role and login realm. */
export const ADMIN_DASHBOARD = "/admin/dashboard";
export const ADMIN_LOGIN = "/admin/login";
export const APP_DASHBOARD = "/dashboard";
export const APP_LOGIN = "/login";

export const LOGIN_REALM = {
    APP: "app",
    ADMIN: "admin",
};

export const SESSION_TYPE = {
    APP: "app",
    ADMIN: "admin",
};

export function getPostLoginPath(user, sessionType = null, fallback = APP_DASHBOARD) {
    if (isSuperadmin(user)) {
        return isAdminSession(sessionType) ? ADMIN_DASHBOARD : ADMIN_LOGIN;
    }
    return fallback;
}

/** Where an already-authenticated user should land from a login portal. */
export function getAuthenticatedLoginRedirect(user, sessionType, { adminMode = false, fromPathname } = {}) {
    if (!isAuthenticatedUser(user)) return null;

    if (adminMode) {
        if (!isSuperadmin(user)) return APP_DASHBOARD;
        if (isAdminSession(sessionType)) return ADMIN_DASHBOARD;
        return null;
    }

    if (isSuperadmin(user)) {
        return isAdminSession(sessionType) ? ADMIN_DASHBOARD : ADMIN_LOGIN;
    }

    return fromPathname || APP_DASHBOARD;
}

export function isSuperadmin(user) {
    return user?.role === "superadmin";
}

export function isAdminSession(sessionType) {
    return sessionType === SESSION_TYPE.ADMIN;
}

export function isAuthPending(authPhase) {
    return authPhase === "checking" || authPhase === "redirecting";
}

/** Auth context may use `false` during transitions — only plain objects are valid users. */
export function isAuthenticatedUser(user) {
    return !!user && typeof user === "object";
}

/** Protected routes/pages should wait until auth is resolved and user is hydrated. */
export function isProtectedRouteReady(authPhase, user) {
    return !isAuthPending(authPhase) && isAuthenticatedUser(user);
}

export function getRedirectForRealm(realm) {
    return realm === LOGIN_REALM.ADMIN ? ADMIN_DASHBOARD : APP_DASHBOARD;
}

/**
 * Enforce separated login flows — superadmin only via admin realm, app users only via app realm.
 */
export function validateLoginRealm(user, realm) {
    if (realm === LOGIN_REALM.APP && isSuperadmin(user)) {
        return {
            allowed: false,
            reason: "superadmin_on_app_login",
            message: "Use admin login portal",
            redirectTo: ADMIN_LOGIN,
        };
    }
    if (realm === LOGIN_REALM.ADMIN && !isSuperadmin(user)) {
        return {
            allowed: false,
            reason: "non_admin_on_admin_login",
            message: "This portal is for platform administrators only.",
            redirectTo: APP_LOGIN,
        };
    }
    return { allowed: true };
}

/** Map backend login rejection to the correct portal redirect. */
export function getLoginPortalRedirect(status, message, realm = LOGIN_REALM.APP) {
    if (status === 403 && message === "Use admin login portal") {
        return { redirectTo: ADMIN_LOGIN, reason: "superadmin_on_app_login" };
    }
    if (status === 403 && realm === LOGIN_REALM.ADMIN && /administrator/i.test(message || "")) {
        return { redirectTo: APP_LOGIN, reason: "non_admin_on_admin_login" };
    }
    return null;
}
